import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  Users,
  UserPlus,
  Search,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronRight as ArrowRight,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { eventTeamApiService } from '../../../services/eventTeamApi';
import { usernameCacheService } from '../../../services/usernameCache';
import type { EventMemberRole, EventMemberStatus, EventTeamMember } from '../../../types/eventTeam';
import { EVENT_MEMBER_ROLES, EVENT_MEMBER_STATUSES } from '../../../types/eventTeam';
import type { EventTeamPerms } from '../../../utils/eventTeamPermissions';
import { EventMemberModal } from '../EventMemberModal';
import { ConfirmationModal } from '../../ConfirmationModal';
import { PlayerProfile } from '../../PlayerProfile';
import { useDebounce } from '../../../hooks/useDebounce';
import { cn } from '../../../utils/cn';
import { SectionHeader } from '../../ui/SectionHeader';
import { EmptyState } from '../../ui/EmptyState';
import { SkeletonLoader } from '../../SkeletonLoader';

const PAGE_SIZE = 50;

const ROLE_CHIP: Record<EventMemberRole, string> = {
  organizer: 'bg-blue-500/15 text-blue-300 border-blue-500/25',
  host: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/25',
  lead: 'bg-violet-500/15 text-violet-300 border-violet-500/25',
  manager: 'bg-amber-500/15 text-amber-300 border-amber-500/25',
  admin: 'bg-red-500/15 text-red-300 border-red-500/25',
};

const STATUS_CHIP: Record<EventMemberStatus, string> = {
  active: 'bg-green-500/15 text-green-300 border-green-500/25',
  inactive: 'bg-glass-200/10 text-glass-400 border-glass-200/20',
  on_leave: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/25',
};

function parseSpecialties(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
}

export interface EventRosterPanelProps {
  perms: EventTeamPerms;
  onOpenMember: (id: string) => void;
}

/** Team Roster (guide §1.7): list/search/count + admin CRUD; drill-down per member. */
export const EventRosterPanel = ({ perms, onOpenMember }: EventRosterPanelProps) => {
  const [members, setMembers] = useState<EventTeamMember[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [roleFilter, setRoleFilter] = useState<EventMemberRole | ''>('');
  const [statusFilter, setStatusFilter] = useState<EventMemberStatus | ''>('');

  const [showCreate, setShowCreate] = useState(false);
  const [editingMember, setEditingMember] = useState<EventTeamMember | null>(null);
  const [memberToDelete, setMemberToDelete] = useState<EventTeamMember | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  /** Overall count from the dedicated `GET /events/team/count` endpoint. */
  const [overallCount, setOverallCount] = useState<number | null>(null);

  useEffect(() => {
    eventTeamApiService
      .countTeamMembers()
      .then((res) => setOverallCount(res.count))
      .catch(() => setOverallCount(null));
  }, []);

  const load = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      setError(null);
      try {
        let loaded: EventTeamMember[];
        if (debouncedSearch.trim()) {
          const results = await eventTeamApiService.searchTeamMembers(debouncedSearch.trim());
          loaded = results.filter(
            (m) => (!roleFilter || m.role === roleFilter) && (!statusFilter || m.status === statusFilter),
          );
          setMembers(loaded);
          setTotal(loaded.length);
          setPage(1);
        } else {
          const res = await eventTeamApiService.listTeamMembers({
            role: roleFilter || undefined,
            status: statusFilter || undefined,
            page: targetPage,
            limit: PAGE_SIZE,
          });
          loaded = res.results;
          setMembers(loaded);
          setTotal(res.total);
          setPage(targetPage);
        }
        usernameCacheService.prefetchUsernames(loaded.map((m) => m.minecraft_uuid));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load roster');
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch, roleFilter, statusFilter],
  );

  useEffect(() => {
    void load(1);
  }, [load]);

  const handleDelete = async () => {
    if (!memberToDelete) return;
    setIsDeleting(true);
    try {
      await eventTeamApiService.deleteTeamMember(memberToDelete.id);
      setMemberToDelete(null);
      await load(page);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete team member');
    } finally {
      setIsDeleting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4">
      <SectionHeader
        as="h2"
        title="Team Roster"
        icon={Users}
        iconClassName="text-purple-400"
        count={overallCount != null && overallCount !== total ? `${total} of ${overallCount}` : total}
        className="mb-0 flex-wrap"
        actions={
          perms.canAdmin ? (
            <button type="button" onClick={() => setShowCreate(true)} className="btn-primary">
              <UserPlus className="w-4 h-4" />
              Add member
            </button>
          ) : undefined
        }
      />

      {/* Filters */}
      <div className="flex flex-col md:flex-row md:items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search members…"
            className="glass-input w-full pl-9"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value as EventMemberRole | '')}
          className="glass-select md:w-44 capitalize"
          aria-label="Filter by role"
        >
          <option value="">All roles</option>
          {EVENT_MEMBER_ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as EventMemberStatus | '')}
          className="glass-select md:w-44 capitalize"
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          {EVENT_MEMBER_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" aria-busy="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="glass-card p-4 space-y-2">
              <SkeletonLoader className="h-5 w-40" />
              <div className="flex gap-2">
                <SkeletonLoader className="h-5 w-16 rounded-full" />
                <SkeletonLoader className="h-5 w-16 rounded-full" />
              </div>
              <SkeletonLoader className="h-3 w-32" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="alert alert-danger" role="alert">
          <AlertCircle />
          <span className="flex-1">{error}</span>
        </div>
      ) : members.length === 0 ? (
        <EmptyState icon={Users} title="No roster members" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {members.map((member) => {
            const specialties = parseSpecialties(member.specialties);
            return (
              <div key={member.id} className="glass-card p-4">
                <div className="flex items-start justify-between gap-2">
                  <button type="button" onClick={() => onOpenMember(member.id)} className="min-w-0 text-left group">
                    <div className="flex items-center gap-2 flex-wrap">
                      <PlayerProfile
                        uuid={member.minecraft_uuid}
                        name={member.display_name}
                        showUuid={false}
                        inline
                      />
                      <ArrowRight className="w-3.5 h-3.5 text-glass-500 group-hover:text-accent-300 transition-colors" />
                    </div>
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      <span className={cn('badge capitalize', ROLE_CHIP[member.role])}>
                        {member.role}
                      </span>
                      <span className={cn('badge badge-dot capitalize', STATUS_CHIP[member.status])}>
                        {member.status.replace(/_/g, ' ')}
                      </span>
                      {specialties.map((s) => (
                        <span
                          key={s}
                          className="badge bg-cyan-500/15 text-cyan-300 border-cyan-500/25"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                    <p className="text-[11px] text-glass-500 mt-1.5 tabular-nums">
                      Joined {formatDistanceToNow(new Date(member.joined_at), { addSuffix: true })}
                    </p>
                    {member.notes && (
                      <p className="text-xs text-glass-400 italic mt-1 line-clamp-2">{member.notes}</p>
                    )}
                  </button>
                  {perms.canAdmin && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => setEditingMember(member)}
                        className="btn-icon btn-icon-sm"
                        title="Edit member"
                        aria-label="Edit member"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setMemberToDelete(member)}
                        className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                        title="Remove from roster"
                        aria-label="Remove from roster"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!debouncedSearch && totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => void load(page - 1)}
            disabled={page <= 1 || loading}
            className="btn-icon"
            title="Previous page"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-glass-400 tabular-nums">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => void load(page + 1)}
            disabled={page >= totalPages || loading}
            className="btn-icon"
            title="Next page"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <EventMemberModal
        isOpen={showCreate}
        mode="create"
        onClose={() => setShowCreate(false)}
        onSuccess={() => void load(1)}
      />
      {editingMember && (
        <EventMemberModal
          isOpen={!!editingMember}
          mode="edit"
          member={editingMember}
          onClose={() => setEditingMember(null)}
          onSuccess={() => {
            setEditingMember(null);
            void load(page);
          }}
        />
      )}
      {memberToDelete && (
        <ConfirmationModal
          isOpen={!!memberToDelete}
          onClose={() => setMemberToDelete(null)}
          onConfirm={() => void handleDelete()}
          title="Remove Team Member"
          message={`Remove ${memberToDelete.display_name} from the event team roster? Their assignment history is preserved but they can no longer self-assign.`}
          confirmText="Remove"
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};
